// src/pages/Trend/components/filters/FilterPanel.js
import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Plus,
  Info,
  BarChart3,
  LayoutDashboard,
  Compass,
  Droplets,
  Heart,
  Wind,
  Sun,
  Sparkles
} from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import './FilterPanel.css';
import categories from '../../../../constants/categories';
import { useQuestions } from '../../../../hooks/useQuestions';
import { useFilterSet } from '../../../../hooks/useFilterSet';
import { FILTER_TRANSLATIONS } from '../../../../constants/translations/filterTranslations';
import TabNavigation from './TabNavigation';
import BrandPanelContent from '../brand/BrandPanelContent';

// Ingredient Input Panel Component (Bolster Style)
const IngredientInputPanel = ({ onAdd, displayText }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    if (inputValue.trim()) {
      onAdd(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAdd();
    }
  };

  return (
    <div className="ingredient-input-panel">
      <div className="ingredient-input-content">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={displayText('예: 히알루론산, 비타민C...', 'questions')}
          className="ingredient-text-input"
          maxLength={100}
        />

        <button
          type="button"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
          className="ingredient-add-btn"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

// Sub-panel header with back button
const SubPanelHeader = ({ title, onBack }) => (
  <div className="sub-panel-header">
    <button className="back-button" onClick={onBack} type="button">
      <ChevronLeft size={18} />
    </button>
    <span className="sub-panel-title">{title}</span>
  </div>
);

export const FilterPanel = ({
  onFiltersApply,
  initialCategoryId = 'CM01',
  activeTab,
  onTabChange,
  brandState
}) => {
  const { translate, language } = useLanguage();

  // View state: 'menu' or 'subpanel'
  const [currentView, setCurrentView] = useState('menu');
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [currentTab, setCurrentTab] = useState('dashboard');

  const [currentCategoryId, setCurrentCategoryId] = useState(initialCategoryId);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [activeSet, setActiveSet] = useState('main');
  const [showComparisonSet, setShowComparisonSet] = useState(false);
  const [isApplied, setIsApplied] = useState(false);
  const [showQuestionDropdown, setShowQuestionDropdown] = useState(false);

  const questionDropdownRef = useRef(null);
  const comparisonDropdownRef = useRef(null);

  const {
    filters: mainFilters,
    updateFilter: updateMainFilter,
    resetFilters: resetMainFilters
  } = useFilterSet();

  const {
    filters: comparisonFilters,
    updateFilter: updateComparisonFilter,
    resetFilters: resetComparisonFilters
  } = useFilterSet();

  const questions = useQuestions(currentCategoryId);

  // Handle tab selection - navigate to sub-panel only if needed
  const handleTabSelect = useCallback((tabId, hasSubPanel) => {
    setCurrentTab(tabId);
    if (hasSubPanel) {
      setSelectedPanel(tabId);
      setCurrentView('subpanel');
    }
    if (onTabChange) {
      onTabChange(tabId);
    }
  }, [onTabChange]);

  // Handle back button - return to menu
  const handleBack = useCallback(() => {
    setCurrentView('menu');
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideMainDropdown = !questionDropdownRef.current || !questionDropdownRef.current.contains(event.target);
      const isOutsideComparisonDropdown = !comparisonDropdownRef.current || !comparisonDropdownRef.current.contains(event.target);

      if (isOutsideMainDropdown && isOutsideComparisonDropdown) {
        setShowQuestionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = useCallback((text, type = 'questions') => {
    if (language === 'ko') return text;

    const languageTranslations = FILTER_TRANSLATIONS[language];
    if (!languageTranslations) return text;

    const translationMap = type === 'categories'
      ? languageTranslations.categories
      : type === 'options'
        ? languageTranslations.options
        : languageTranslations.questions;

    return translationMap?.[text] || text;
  }, [language]);

  const getCategoryIcon = useCallback((categoryId) => {
    const iconMap = {
      'CM01': Droplets,  // Skincare
      'CM02': Heart,     // Bodycare
      'CM03': Wind,      // Haircare
      'CM04': Sun,       // Sun care
      'CM05': Sparkles   // Cleansing
    };
    return iconMap[categoryId] || Droplets;
  }, []);

  const displayCategories = useCallback(() => {
    return categories
      .filter(category => category.category_name !== 'Common')
      .map(category => ({
        ...category,
        category_name: displayText(category.category_name, 'categories')
      }));
  }, [displayText]);

  const handleCategoryChange = useCallback((categoryId) => {
    setCurrentCategoryId(categoryId);
    resetMainFilters();
    resetComparisonFilters();
    setActiveSet('main');
    setSelectedQuestionId(null);
    setShowComparisonSet(false);
    setIsApplied(false);
  }, [resetMainFilters, resetComparisonFilters]);

  const handleFilterSelect = useCallback((questionText, optionText, setType) => {
    const filterSet = setType || activeSet;

    if (filterSet === 'main') {
      updateMainFilter(questionText, optionText);
    } else if (filterSet === 'comparison') {
      updateComparisonFilter(questionText, optionText);
    }
  }, [activeSet, updateMainFilter, updateComparisonFilter]);

  const handleToggleComparison = useCallback(() => {
    if (showComparisonSet) {
      setShowComparisonSet(false);
      setActiveSet('main');
      resetComparisonFilters();
    } else {
      setShowComparisonSet(true);
      setActiveSet('comparison');
    }
  }, [showComparisonSet, resetComparisonFilters]);

  const handleClearMainFilters = useCallback(() => {
    resetMainFilters();
  }, [resetMainFilters]);

  const handleClearComparisonFilters = useCallback(() => {
    resetComparisonFilters();
  }, [resetComparisonFilters]);

  const handleReset = useCallback(() => {
    resetMainFilters();
    resetComparisonFilters();
    setSelectedQuestionId(null);
    setShowComparisonSet(false);
    setActiveSet('main');
    setIsApplied(false);
  }, [resetMainFilters, resetComparisonFilters]);

  const handleApply = useCallback(() => {
    console.log('🚀 Sending to API:', {
      categoryId: currentCategoryId,
      mainFilters,
      comparisonFilters: showComparisonSet ? comparisonFilters : null
    });

    onFiltersApply(
      currentCategoryId,
      mainFilters,
      showComparisonSet ? comparisonFilters : null
    );

    setIsApplied(true);
    setSelectedQuestionId(null);
    setShowQuestionDropdown(false);
  }, [currentCategoryId, mainFilters, comparisonFilters, showComparisonSet, onFiltersApply]);

  const handleModify = useCallback(() => {
    setIsApplied(false);
  }, []);

  const handleQuestionSelect = useCallback((questionId) => {
    setSelectedQuestionId(questionId);
    setShowQuestionDropdown(false);
  }, []);

  const getSelectedQuestion = useCallback(() => {
    return questions.find(q => q.question_id === selectedQuestionId);
  }, [questions, selectedQuestionId]);

  const renderFilterTags = (filters, isComparison = false) => {
    const entries = Object.entries(filters);
    if (entries.length === 0) {
      return (
        <span className="empty-filters-text">
          {translate('trendPage.filters.noFilters') || 'No filters selected'}
        </span>
      );
    }

    return entries.map(([questionText, optionText]) => (
      <span
        key={questionText}
        className={`filter-tag ${isComparison ? 'comparison' : 'main'}`}
      >
        {displayText(optionText, 'options')}
        {!isApplied && (
          <button
            onClick={() => handleFilterSelect(questionText, optionText, isComparison ? 'comparison' : 'main')}
            className="filter-tag-remove"
          >
            <X size={12} />
          </button>
        )}
      </span>
    ));
  };

  // Get panel title based on selected panel
  const getPanelTitle = () => {
    switch (selectedPanel) {
      case 'dashboard':
        return translate('trendPage.tabs.dashboard') || 'Dashboard';
      case 'brand':
        return translate('trendPage.tabs.brand') || 'Brand';
      case 'analysis':
        return translate('trendPage.tabs.analysis') || 'Analysis';
      default:
        return '';
    }
  };

  // Dashboard placeholder component
  const DashboardPlaceholder = () => (
    <div className="dashboard-placeholder">
      <LayoutDashboard size={48} className="dashboard-placeholder-icon" />
      <p className="dashboard-placeholder-text">
        {translate('trendPage.dashboard.placeholder') || 'Dashboard coming soon'}
      </p>
    </div>
  );

  // Render Analysis filter content
  const renderAnalysisContent = () => (
    <>
      {/* Category Selection */}
      <div className="filter-section">
        <div className="filter-section-header">
          <span className="filter-section-label">
            {translate('trendPage.filters.category') || 'Category'}
          </span>
          <button className="info-button">
            <Info size={14} />
          </button>
        </div>

        <div className="category-radio-group">
          {displayCategories().map(category => {
            const IconComponent = getCategoryIcon(category.category_id);
            return (
              <label
                key={category.category_id}
                className={`category-radio-option ${category.category_id === currentCategoryId ? 'active' : ''}`}
                data-category={category.category_id}
              >
                <input
                  type="radio"
                  name="category"
                  value={category.category_id}
                  checked={category.category_id === currentCategoryId}
                  onChange={() => handleCategoryChange(category.category_id)}
                  className="category-radio-input"
                />
                <div className="category-radio-content">
                  <IconComponent size={16} className="category-radio-icon" />
                  <span className="category-radio-label">{category.category_name}</span>
                </div>
                {category.category_id === currentCategoryId && (
                  <Check size={16} className="category-radio-check" />
                )}
              </label>
            );
          })}
        </div>
      </div>

      {/* Main Filters Section */}
      <div className="filter-section">
        <div className="filter-section-header">
          <span className="filter-section-label">
            {translate('trendPage.filters.set1') || 'Main Filters'}
          </span>
          {Object.keys(mainFilters).length > 0 && !isApplied && (
            <button
              className="clear-filters-button"
              onClick={handleClearMainFilters}
            >
              {translate('trendPage.buttons.clearAll') || 'Clear All'}
            </button>
          )}
        </div>

        <div className="filter-tags-container">
          <div className="filter-tags-wrapper">
            {renderFilterTags(mainFilters)}
          </div>
        </div>

        {/* Add Filter Dropdown */}
        {!isApplied && Object.keys(mainFilters).length < 4 && (
          <div className="question-dropdown" ref={questionDropdownRef}>
            <button
              className="add-filter-button"
              onClick={() => {
                setActiveSet('main');
                setShowQuestionDropdown(!showQuestionDropdown);
              }}
            >
              <Plus size={16} />
              {translate('trendPage.filters.addFilter') || 'Add Filter'}
            </button>

            {showQuestionDropdown && activeSet === 'main' && (
              <div className="question-dropdown-menu">
                {questions.map(question => (
                  <button
                    key={question.question_id}
                    className={`question-dropdown-item ${selectedQuestionId === question.question_id ? 'active' : ''}`}
                    onClick={() => handleQuestionSelect(question.question_id)}
                  >
                    {displayText(question.question_text)}
                    <ChevronRight size={14} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Options for selected question */}
        {selectedQuestionId && activeSet === 'main' && !isApplied && (
          <div className="options-panel">
            {(() => {
              const selectedQuestion = getSelectedQuestion();

              if (selectedQuestion?.question_text === '주요 성분' && selectedQuestion?.options.length === 0) {
                return (
                  <IngredientInputPanel
                    onAdd={(ingredientText) => {
                      handleFilterSelect(selectedQuestion.question_text, ingredientText);
                    }}
                    displayText={displayText}
                  />
                );
              }

              return (
                <>
                  <div className="options-panel-header">
                    {displayText(selectedQuestion.question_text)}
                  </div>
                  <div className="options-grid">
                    {selectedQuestion?.options.map(option => {
                      const isSelected = mainFilters[selectedQuestion.question_text] === option.option_text;
                      const isDisabled = Object.keys(mainFilters).length >= 4 && !mainFilters[selectedQuestion.question_text];

                      return (
                        <button
                          key={option.option_number}
                          onClick={() => handleFilterSelect(selectedQuestion.question_text, option.option_text)}
                          disabled={isDisabled}
                          className={`option-button ${isSelected ? 'selected' : ''}`}
                        >
                          <span>{displayText(option.option_text, 'options')}</span>
                          {isSelected && <Check size={14} />}
                        </button>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Comparison Filters Section */}
      <div className="filter-section">
        <div className="filter-section-header">
          <span className="filter-section-label">
            {translate('trendPage.filters.set2') || 'Comparison'}
          </span>
          <button
            className={`toggle-switch ${showComparisonSet ? 'active' : ''}`}
            onClick={handleToggleComparison}
          >
            <span className="toggle-knob"></span>
          </button>
        </div>

        {showComparisonSet && (
          <>
            {Object.keys(comparisonFilters).length > 0 && !isApplied && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                <button
                  className="clear-filters-button"
                  onClick={handleClearComparisonFilters}
                >
                  {translate('trendPage.buttons.clearAll') || 'Clear All'}
                </button>
              </div>
            )}

            <div className="filter-tags-container comparison">
              <div className="filter-tags-wrapper">
                {renderFilterTags(comparisonFilters, true)}
              </div>
            </div>

            {/* Add Filter for Comparison */}
            {!isApplied && Object.keys(comparisonFilters).length < 4 && (
              <div className="question-dropdown comparison" ref={comparisonDropdownRef}>
                <button
                  className="add-filter-button comparison"
                  onClick={() => {
                    setActiveSet('comparison');
                    setShowQuestionDropdown(!showQuestionDropdown);
                  }}
                >
                  <Plus size={16} />
                  {translate('trendPage.filters.addFilter') || 'Add Filter'}
                </button>

                {showQuestionDropdown && activeSet === 'comparison' && (
                  <div className="question-dropdown-menu comparison">
                    {questions.map(question => (
                      <button
                        key={question.question_id}
                        className={`question-dropdown-item ${selectedQuestionId === question.question_id ? 'active' : ''}`}
                        onClick={() => handleQuestionSelect(question.question_id)}
                      >
                        {displayText(question.question_text)}
                        <ChevronRight size={14} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Options for comparison */}
            {selectedQuestionId && activeSet === 'comparison' && !isApplied && (
              <div className="options-panel comparison">
                {(() => {
                  const selectedQuestion = getSelectedQuestion();

                  if (selectedQuestion?.question_text === '주요 성분' && selectedQuestion?.options.length === 0) {
                    return (
                      <IngredientInputPanel
                        onAdd={(ingredientText) => {
                          handleFilterSelect(selectedQuestion.question_text, ingredientText);
                        }}
                        displayText={displayText}
                      />
                    );
                  }

                  return (
                    <>
                      <div className="options-panel-header">
                        {displayText(selectedQuestion.question_text)}
                      </div>
                      <div className="options-grid">
                        {selectedQuestion?.options.map(option => {
                          const isSelected = comparisonFilters[selectedQuestion.question_text] === option.option_text;
                          const isDisabled = Object.keys(comparisonFilters).length >= 4 && !comparisonFilters[selectedQuestion.question_text];

                          return (
                            <button
                              key={option.option_number}
                              onClick={() => handleFilterSelect(selectedQuestion.question_text, option.option_text)}
                              disabled={isDisabled}
                              className={`option-button comparison ${isSelected ? 'selected' : ''}`}
                            >
                              <span>{displayText(option.option_text, 'options')}</span>
                              {isSelected && <Check size={14} />}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  return (
    <aside className="filter-panel">
      {/* Panel Header */}
      <div className="filter-panel-header">
        <div className="filter-panel-title">
          <div className="filter-panel-title-icon">
            <Compass size={14} />
          </div>
          <span>{translate('trendPage.panel.title') || 'Explorer'}</span>
        </div>
      </div>

      {/* Slide Container for Menu and Sub-panels */}
      <div className="filter-panel-slide-container">
        {/* Menu View - Tab Navigation */}
        <div className={`panel-slide-view menu-view ${currentView === 'menu' ? 'visible' : 'hidden'}`}>
          <div className="filter-panel-content">
            <TabNavigation activeTab={currentTab} onTabSelect={handleTabSelect} />
          </div>
        </div>

        {/* Sub-panel View */}
        <div className={`panel-slide-view sub-panel-view ${currentView === 'subpanel' ? 'visible' : 'hidden'}`}>
          <div className="filter-panel-content">
            <SubPanelHeader title={getPanelTitle()} onBack={handleBack} />

            {/* Dashboard Tab */}
            {selectedPanel === 'dashboard' && <DashboardPlaceholder />}

            {/* Brand Tab */}
            {selectedPanel === 'brand' && brandState && (
              <BrandPanelContent
                selectedCategory={brandState.params.category}
                onCategoryChange={brandState.handleCategoryChange}
              />
            )}

            {/* Analysis Tab */}
            {selectedPanel === 'analysis' && renderAnalysisContent()}
          </div>

          {/* Panel Footer - Only show for Analysis tab */}
          {selectedPanel === 'analysis' && (
            <div className="filter-panel-footer">
              <button
                className="apply-filters-button"
                onClick={isApplied ? handleModify : handleApply}
                disabled={!isApplied && Object.keys(mainFilters).length === 0 && Object.keys(comparisonFilters).length === 0}
              >
                <BarChart3 size={16} />
                {isApplied
                  ? (translate('trendPage.buttons.modify') || 'Modify Filters')
                  : (translate('trendPage.buttons.apply') || 'Apply Filters')
                }
              </button>
              {!isApplied && (Object.keys(mainFilters).length > 0 || Object.keys(comparisonFilters).length > 0) && (
                <button
                  className="reset-filters-button"
                  onClick={handleReset}
                >
                  {translate('trendPage.buttons.reset') || 'Reset'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

FilterPanel.propTypes = {
  onFiltersApply: PropTypes.func.isRequired,
  initialCategoryId: PropTypes.string,
  activeTab: PropTypes.oneOf(['dashboard', 'brand', 'analysis']),
  onTabChange: PropTypes.func,
  brandState: PropTypes.object
};

export default FilterPanel;
